import { graphql } from 'react-apollo'
import { withRouter } from 'react-router'

import { pathOr } from 'ramda'
import {
  compose,
  defaultProps,
  fromRenderProps,
  withHandlers,
  withProps,
  withState,
} from 'recompose'

import withHeroText from 'components/HOCs/withHeroText'
import withPartnerProps from 'components/HOCs/WithPartnerProps'
import withReferralDisclosureRequired from 'components/HOCs/WithReferralDisclosureRequired'
import withRemoteStrings from 'components/HOCs/withRemoteStrings'
import getDefaultEventData from 'components/Trackers/defaults'
import { TRACKER_IDS } from 'components/Trackers/trackerIDs'
import { ActionType, PageName } from 'generated/types'
import { getButtonTappedEvent } from 'utils/analytics'
import trackEvent from 'utils/analytics/trackEvent'
import trackGAEvent from 'utils/analytics/trackGAEvent'
import { withApolloErrorModal } from 'utils/Apollo'
import { sendToSentry } from 'utils/errors'
import { withFlowRouter } from 'utils/FlowProvider'
import { FlowRoute } from 'utils/FlowProvider/types'
import { FunnelContext, FunnelContextProps } from 'utils/FunnelContext'
import { getGeneralError } from 'utils/getGeneralError/getGeneralError'
import { withOptimizely } from 'utils/Optimizely'

import initialStateQuery from '../gql/initialState.query.gql'
import updateRecurringInvestmentMutation from '../gql/updateRecurringInvestment.mutation.gql'
import { InnerProps, InvestmentMode } from '../types'
import { readUserSettings, saveUserSettings } from '../utilities'

export const RECURRING_AMOUNTS = ['100', '50', '20', '10', '5']
export const DEFAULT_RECURRING = '20'

export const investmentAmount = (props: any) => {
  return props.recurringAmount || DEFAULT_RECURRING
}

const enhance = compose<InnerProps, {}>(
  defaultProps({
    loading: true,
    investmentMode: InvestmentMode.Recurring,
  }),
  fromRenderProps<Partial<FunnelContextProps>, {}, Partial<FunnelContextProps>>(
    FunnelContext.Consumer,
    ({ isDynamicFunnelEnabled, routeToPage }) => ({ isDynamicFunnelEnabled, routeToPage }),
  ),
  withReferralDisclosureRequired(),
  withFlowRouter,
  withRouter,
  withHeroText,
  withPartnerProps,
  withApolloErrorModal,
  withRemoteStrings,
  withOptimizely,
  withState(
    'recurringAmount',
    'setRecurringAmount',
    readUserSettings('recurringAmount', DEFAULT_RECURRING),
  ),
  withState('confirmModalOpen', 'setConfirmModalOpen', false),
  withState('noFundingSourceModalOpen', 'setNoFundingSourceModalOpen', false),
  withState('submitting', 'setSubmitting', false),
  graphql(updateRecurringInvestmentMutation, { name: 'updateRecurringInvestment' }),
  graphql(initialStateQuery, {
    options: {
      fetchPolicy: 'network-only',
    },
    props: props => {
      const { data } = props
      return {
        loading: data.loading,
        fundingSource: pathOr(null, ['fundingSourceInfo'], data),
        laterAccountId: pathOr(null, ['laterAccounts', 0, 'accountId'], data),
        recurringAmount: readUserSettings(
          'recurringAmount',
          pathOr(DEFAULT_RECURRING, ['recurringInvestmentSettings', 'amount'], data).toString(),
        ),
        investmentMode: InvestmentMode.Recurring,
      }
    },
  }),
  withProps(({ getRemoteString, heroText }) => {
    return {
      header: getRemoteString('recurring_investment_header', heroText.header),
      text: getRemoteString('recurring_investment_text', heroText.text),
    }
  }),
  withHandlers<InnerProps, {}>({
    handleCTA: ({
      setConfirmModalOpen,
      setNoFundingSourceModalOpen,
      fundingSource,
      flow,
      partner,
    }) => () => {
      trackEvent('Button Tapped', {
        eventData: {
          ...getButtonTappedEvent(
            TRACKER_IDS.START_INVESTING.REGISTRATION_RECURRING_INVESTMENT_SET_DEFAULT,
          ),
          ...getDefaultEventData('Button Tapped'),
        },
        flow,
        partner,
      })

      if (fundingSource) {
        setConfirmModalOpen(true)
        trackEvent('Screen Viewed', {
          eventData: {
            feature: 'Registration Initial Investment: Recurring',
            event_description: 'Confirm Amount modal viewed',
            object_name: 'registrationInitialInvestmentRecurringConfirmAmount',
            style: 'modal',
            screen: 'registrationInitialInvestmentRecurring',
            screen_name: 'registrationInitialInvestmentRecurringConfirmAmount',
            object: 'Screen',
            action: 'Viewed',
            ...getDefaultEventData('Screen Viewed'),
          },
        })
      } else {
        setNoFundingSourceModalOpen(true)
      }
      trackEvent('Button Tapped', {
        eventData: {
          ...getButtonTappedEvent(TRACKER_IDS.START_INVESTING.RECURRING_START_INVESTING),
          ...getDefaultEventData('Button Tapped'),
        },
        flow,
        partner,
      })
    },
    handleSkip: ({
      route,
      flow,
      partner,
      referralDisclosureRequired,
      isDynamicFunnelEnabled,
      routeToPage,
    }) => (action: 'continue' | 'one-time') => () => {
      saveUserSettings()

      trackEvent('Button Tapped', {
        eventData: {
          ...getButtonTappedEvent(
            action === 'continue'
              ? TRACKER_IDS.START_INVESTING.REGISTRATION_RECURRING_INVESTMENT_SKIP
              : TRACKER_IDS.START_INVESTING.REGISTRATION_RECURRING_INVESTMENT_ONE_TIME,
          ),
          ...getDefaultEventData('Button Tapped'),
        },
        flow,
        partner,
      })

      trackEvent('Button Tapped', {
        eventData: {
          ...getButtonTappedEvent(TRACKER_IDS.START_INVESTING.RECURRING_SKIP),
          ...getDefaultEventData('Button Tapped'),
        },
        flow,
        partner,
      })

      if (isDynamicFunnelEnabled) {
        if (action === 'one-time') {
          routeToPage(PageName.RecurringInvestment, ActionType.Switch)
        } else {
          referralDisclosureRequired
            ? routeToPage(PageName.RecurringInvestment, ActionType.MoreInfo)
            : routeToPage(PageName.RecurringInvestment, ActionType.Continue)
        }
      } else {
        route({
          from: FlowRoute.RecurringInvestment,
          action,
          state: { referralDisclosureRequired },
        })
      }
    },
    handleLinkAccount: ({ route: route, isDynamicFunnelEnabled, routeToPage }) => () => {
      if (isDynamicFunnelEnabled) {
        routeToPage(PageName.Institutions, ActionType.Link)
      } else {
        route({ from: FlowRoute.RecurringInvestment, action: 'link-account' })
      }
    },
    handleRecurringAmountChange: ({
      setRecurringAmount,
      oneTimeAmount,
      investmentMode,
      flow,
      partner,
    }) => ({ currentTarget }) => {
      saveUserSettings({
        recurringAmount: currentTarget.dataset.value,
        oneTimeAmount,
        investmentMode,
      })
      setRecurringAmount(currentTarget.dataset.value)
      trackEvent('Button Tapped', {
        eventData: {
          ...getButtonTappedEvent(TRACKER_IDS.START_INVESTING.CHANGE_RECURRING_AMOUNT),
          ...getDefaultEventData('Button Tapped'),
          cta_title: currentTarget.dataset.value,
        },
        flow,
        partner,
      })
    },
    handleModalClosed: ({ setConfirmModalOpen }) => () => {
      trackEvent('Button Tapped', {
        eventData: {
          feature: 'Registration Initial Investment: Recurring',
          event_description: 'Confirm Amount modal dismissed',
          object_name: 'registrationInitialInvestmentRecurringConfirmAmountCancel',
          style: 'default',
          screen: 'registrationInitialInvestmentRecurring',
          screen_name: 'registrationInitialInvestmentRecurringConfirmAmount',
          functionality: 'dismiss',
          object: 'Button',
          action: 'Tapped',
          ...getDefaultEventData('Button Tapped'),
        },
      })
      setConfirmModalOpen(false)
    },
    handleModalConfirmed: ({
      route: route,
      referralDisclosureRequired,
      updateRecurringInvestment,
      investmentMode,
      recurringAmount,
      oneTimeAmount,
      setConfirmModalOpen,
      setSubmitting,
      trackOptimizely,
      closeErrorModal,
      showErrorModal,
      isDynamicFunnelEnabled,
      routeToPage,
    }) => () => {
      setConfirmModalOpen(false)
      setSubmitting(true)

      trackEvent('Button Tapped', {
        eventData: {
          feature: 'Registration Initial Investment: Recurring',
          event_description: 'Confirm Amount modal tapped',
          object_name: 'registrationInitialInvestmentRecurringConfirmAmountConfirm',
          style: 'default',
          screen: 'registrationInitialInvestmentRecurring',
          screen_name: 'registrationInitialInvestmentRecurringConfirmAmount',
          destination: 'registrationInvestorCertificate',
          functionality: 'setRecurring',
          object: 'Button',
          action: 'Tapped',
          cta_title: 'OK',
          frequency: 'weekly',
          amount: parseFloat(investmentAmount({ investmentMode, recurringAmount, oneTimeAmount })),
          ...getDefaultEventData('Button Tapped'),
        },
      })

      const input = {
        amount: parseFloat(investmentAmount({ investmentMode, recurringAmount, oneTimeAmount })),
        day: new Date().getDay() + 1,
        frequency: 'weekly',
      }

      const variables = { input }

      const makeRecurringInvestment = () => {
        updateRecurringInvestment({ variables })
          .then(() => {
            saveUserSettings()
            trackOptimizely('bitcoin_recurring_deposit_initated')
            trackOptimizely('recurring_investment.complete')
            trackGAEvent('recurringDepositScheduled')
            if (isDynamicFunnelEnabled) {
              referralDisclosureRequired
                ? routeToPage(PageName.RecurringInvestment, ActionType.MoreInfo, {
                    investment: input,
                  })
                : routeToPage(PageName.RecurringInvestment, ActionType.Continue, {
                    investment: input,
                  })
            } else {
              route({
                from: FlowRoute.RecurringInvestment,
                action: 'continue',
                state: { referralDisclosureRequired },
                locationState: { investment: input },
              })
            }
          })
          .catch(err => {
            sendToSentry(err)

            const retryHandler = () => {
              closeErrorModal()
              makeRecurringInvestment()
            }
            const { title, message, actions, image } = getGeneralError(
              err,
              { closeErrorModal },
              retryHandler,
            )
            showErrorModal({ title, message, actions, image })
          })
          .finally(() => {
            setSubmitting(false)
          })
      }

      makeRecurringInvestment()
    },
  }),
)

export default enhance
